import is from "@sindresorhus/is";
import axios from "axios";
import { XMLParser } from "fast-xml-parser";
import { decode } from "html-entities";
import parse from "html-react-parser";
import { DateTime } from "luxon";

import wpGetCategories from "./actions/wpGetCategories";
import wpGetPostsByCategoryId from "./actions/wpGetPostsByCategoryId";

const SLUG_ADDRESS = "address";
const SLUG_CLINIC = "clinic";
const SLUG_EMAIL = "email";
const SLUG_FEEDBACK = "feedback";
const SLUG_GALLERY = "gallery";
const SLUG_HIGHLIGHTED_POST = "highlighted_post";
const SLUG_OPENING_HOURS = "opening_hours";
const SLUG_OPENING_HOURS_SHORT = "opening_hours_short";
const SLUG_POST = "post";
const SLUG_PHONE_NUMBER = "phone_number";
const SLUG_SERVICE = "service";
const SLUG_WHO_WE_ARE = "who_we_are";

const WP_API_BASE = "/wordpress/wp-json/wp/v2";

const _getBaseItems = (axios, cache) =>
  new Promise((resolve, reject) => {
    const categoryIds = [];
    const categorySlugs = [
      SLUG_ADDRESS,
      SLUG_CLINIC,
      SLUG_EMAIL,
      SLUG_GALLERY,
      SLUG_PHONE_NUMBER,
      SLUG_OPENING_HOURS,
      SLUG_OPENING_HOURS_SHORT,
      SLUG_WHO_WE_ARE
    ];

    Promise.resolve()
      .then(
        () =>
          new Promise((resolve, reject) =>
            categorySlugs
              .reduce(
                (promise, slug) =>
                  promise //
                    .then(() => _getCategoryIdBySlug(axios, cache, slug))
                    .then(categoryId => categoryIds.push(is.number(categoryId) ? categoryId : null)),
                Promise.resolve()
              )
              .then(() => resolve())
              .catch(error => reject(error))
          )
      )
      .then(() => (is.nonEmptyArray(categoryIds) ? _getPostsByCategoryId(axios, cache, categoryIds, { perPage: 100 }) : null))
      .then(posts => {
        posts.forEach(post => {
          const slug = post.categorySlugs.find(postCategorySlug => categorySlugs.some(categorySlug => categorySlug === postCategorySlug));

          switch (slug) {
            case SLUG_ADDRESS:
              cache.addressPost = post;
              break;
            case SLUG_CLINIC:
              cache.clinicPost = post;
              break;
            case SLUG_EMAIL:
              cache.emailPost = post;
              break;
            case SLUG_GALLERY:
              is.array(cache.galleryPosts) ? cache.galleryPosts.push(post) : (cache.galleryPosts = [post]);
              break;
            case SLUG_OPENING_HOURS:
              cache.openingHoursPost = post;
              break;
            case SLUG_OPENING_HOURS_SHORT:
              cache.openingHoursShortPost = post;
              break;
            case SLUG_PHONE_NUMBER:
              cache.phoneNumberPost = post;
              break;
            case SLUG_WHO_WE_ARE:
              is.array(cache.whoWeArePosts) ? cache.whoWeArePosts.push(post) : (cache.whoWeArePosts = [post]);
              break;
            default:
          }
        });
      })
      .then(() => resolve())
      .catch(error => reject(error));
  });

const _getCategoryIdBySlug = (axios, cache, slug) => {
  const isCached = cache => is.array(cache.categories);
  const loadFromCache = (cache, slug) => cache.categories.find(category => category.slug === slug)?.id ?? null;
  const saveToCache = (cache, data) => (cache.categories = data.map(item => ({ id: item.id, slug: item.slug })));

  return new Promise((resolve, reject) =>
    isCached(cache)
      ? resolve(loadFromCache(cache, slug))
      : Promise.resolve()
          .then(() => wpGetCategories(axios))
          .then(categories => saveToCache(cache, categories))
          .then(() => resolve(loadFromCache(cache, slug)))
          .catch(error => reject(error))
  );
};

const _getPostsByCategoryId = (axios, cache, categoryId, options) =>
  new Promise((resolve, reject) =>
    Promise.resolve()
      .then(() => wpGetPostsByCategoryId(axios, categoryId, options))
      .then(posts =>
        posts.map(post => {
          const result = {
            id: post.id,
            date: DateTime.fromISO(post.date).setLocale("hu").toFormat("yyyy. MMMM dd."),
            title: decode(post.title.rendered),
            content: {
              html: parse(post.content.rendered),
              plain: _getTextsFromRendered(post.content.rendered)
            },
            excerpt: {
              plain: _getTextsFromRendered(post.excerpt.rendered).join(" ")
            },
            featuredMedia: {
              id: post.featured_media,
              sourceUrl: null
            },
            categories: post.categories
          };

          if (is.nonEmptyArray(post?._embedded?.["wp:featuredmedia"])) {
            const featuredMedia = post._embedded["wp:featuredmedia"][0];

            result.featuredMedia.sourceUrl = {
              thumbnail: featuredMedia.media_details?.sizes?.thumbnail?.source_url ?? null,
              medium: featuredMedia.media_details?.sizes?.medium?.source_url ?? null,
              mediumLarge: featuredMedia.media_details?.sizes?.medium_large?.source_url ?? null,
              large: featuredMedia.media_details?.sizes?.large?.source_url ?? null,
              full: featuredMedia.source_url ?? null
            };
          }
          return result;
        })
      )
      .then(posts => _getPostsCategorySlugs(axios, cache, posts))
      .then(posts => resolve(posts))
      .catch(error => reject(error))
  );

const _getPostsCategorySlugs = (axios, cache, posts) =>
  new Promise((resolve, reject) =>
    posts
      .reduce((promise, post) => {
        let result;

        if (is.array(post.categories)) {
          post.categorySlugs = [];
          result = promise.then(
            () =>
              new Promise((resolve, reject) =>
                post.categories
                  .reduce(
                    (promise, categoryId) =>
                      promise //
                        .then(() => _getSlugByCategoryId(axios, cache, categoryId))
                        .then(slug => post.categorySlugs.push(slug)),
                    Promise.resolve()
                  )
                  .then(() => resolve())
                  .catch(error => reject(error))
              )
          );
        } else {
          result = promise;
        }
        return result;
      }, Promise.resolve())
      .then(() => resolve(posts))
      .catch(error => reject(error))
  );

// const _getPostsFeaturedMediaSourceUrl = (axios, posts) =>
//   new Promise((resolve, reject) =>
//     posts
//       .reduce(
//         (promise, post) =>
//           promise
//             .then(() => (post.featuredMedia > 0 ? wpGetMediaById(axios, post.featuredMedia) : null))
//             .then(media => {
//               post.featuredMediaSourceUrl = {
//                 thumbnail: media?.media_details?.sizes?.thumbnail?.source_url ?? null,
//                 medium: media?.media_details?.sizes?.medium?.source_url ?? null,
//                 mediumLarge: media?.media_details?.sizes?.medium_large?.source_url ?? null,
//                 large: media?.media_details?.sizes?.large?.source_url ?? null,
//                 full: media?.source_url ?? null
//               };
//             }),
//         Promise.resolve()
//       )
//       .then(() => resolve(posts))
//       .catch(error => reject(error))
//   );

const _getSlugByCategoryId = (axios, cache, categoryId) => {
  const isCached = cache => is.array(cache.categories);
  const loadFromCache = (cache, categoryId) => cache.categories.find(category => category.id === categoryId)?.slug ?? null;
  const saveToCache = (cache, data) => (cache.categories = data.map(item => ({ id: item.id, slug: item.slug })));

  return new Promise((resolve, reject) =>
    isCached(cache)
      ? resolve(loadFromCache(cache, categoryId))
      : Promise.resolve()
          .then(() => wpGetCategories(axios))
          .then(categories => saveToCache(cache, categories))
          .then(() => resolve(loadFromCache(cache, categoryId)))
          .catch(error => reject(error))
  );
};

const _getStringsFromObject = obj => {
  const result = [];

  if (is.array(obj)) obj.forEach(obj => _getStringsFromObject(obj).forEach(obj => result.push(obj)));
  else if (is.plainObject(obj)) Object.keys(obj).forEach(key => _getStringsFromObject(obj[key]).forEach(obj => result.push(obj)));
  else if (is.string(obj)) result.push(obj);
  else if (is.number(obj)) result.push(obj.toString());
  return result;
};

const _getTextsFromRendered = rendered => {
  let result;

  if (is.string(rendered)) {
    const parser = new XMLParser({ ignoreAttributes: true });
    result = _getStringsFromObject(parser.parse(rendered)).map(s => decode(s));
  } else {
    result = null;
  }
  return result;
};

/* ---- */

const initialize = (axios, cache) =>
  new Promise((resolve, reject) =>
    Promise.resolve()
      .then(() => _getBaseItems(axios, cache))
      .then(() => resolve())
      .catch(error => reject(error))
  );

const getAddressPost = cache => cache.addressPost ?? null;
const getClinicPost = cache => cache.clinicPost ?? null;
const getEmailPost = cache => cache.emailPost ?? null;

const getFeedbackPosts = (axios, cache, page) =>
  new Promise((resolve, reject) =>
    Promise.resolve()
      .then(() => _getCategoryIdBySlug(axios, cache, SLUG_FEEDBACK))
      .then(categoryId => _getPostsByCategoryId(axios, cache, categoryId, { perPage: 4 }))
      .then(posts => resolve(posts))
      .catch(error => reject(error))
  );

const getGalleryPosts = cache => cache.galleryPosts ?? [];

const getHighlightedPosts = (axios, cache) =>
  new Promise((resolve, reject) =>
    Promise.resolve()
      .then(() => _getCategoryIdBySlug(axios, cache, SLUG_HIGHLIGHTED_POST))
      .then(categoryId => _getPostsByCategoryId(axios, cache, categoryId, { perPage: 4 }))
      .then(posts => resolve(posts))
      .catch(error => reject(error))
  );

const getOpeningHoursShortPost = cache => cache.openingHoursShortPost ?? null;
const getOpeningHoursPost = cache => cache.openingHoursPost ?? null;
const getPhoneNumberPost = cache => cache.phoneNumberPost ?? null;

const getPosts = (axios, cache, page) =>
  new Promise((resolve, reject) =>
    Promise.resolve()
      .then(() => _getCategoryIdBySlug(axios, cache, SLUG_POST))
      .then(categoryId => _getPostsByCategoryId(axios, cache, categoryId, { page: page ?? 1, perPage: 4 }))
      .then(posts => resolve(posts))
      .catch(error => reject(error))
  );

const getServicePosts = (axios, cache, page) =>
  new Promise((resolve, reject) =>
    Promise.resolve()
      .then(() => _getCategoryIdBySlug(axios, cache, SLUG_SERVICE))
      .then(categoryId => _getPostsByCategoryId(axios, cache, categoryId, { perPage: 4 }))
      .then(posts => resolve(posts))
      .catch(error => reject(error))
  );

const getWhoWeArePosts = cache => cache.whoWeArePosts ?? [];

const axiosWP = axios.create({ baseURL: WP_API_BASE });
const cache = {
  // addressPost: {}
  // categories: []
  // clinicPost: {}
  // emailPost: {}
  // galleryPosts: []
  // openingHoursPost: {}
  // openingHoursShortPost: {}
  // phoneNumberPost: {}
  // whoWeArePosts: []
};

export default {
  initialize: () => initialize(axiosWP, cache),
  getAddressPost: () => getAddressPost(cache),
  getClinicPost: () => getClinicPost(cache),
  getEmailPost: () => getEmailPost(cache),
  getFeedbackPosts: () => getFeedbackPosts(axiosWP, cache),
  getGalleryPosts: () => getGalleryPosts(cache),
  getHighlightedPosts: () => getHighlightedPosts(axiosWP, cache),
  getOpeningHoursPost: () => getOpeningHoursPost(cache),
  getOpeningHoursShortPost: () => getOpeningHoursShortPost(cache),
  getPhoneNumberPost: () => getPhoneNumberPost(cache),
  getPosts: page => getPosts(axiosWP, cache, page),
  getServicePosts: () => getServicePosts(axiosWP, cache),
  getWhoWeArePosts: () => getWhoWeArePosts(cache)
};
